import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Output } from "valibot";

import ChatPanel from "components/ChatPanel";
import PopupMessages from "components/PopupMessages";
import WidgetButton from "components/WidgetButton";
import {
  useChatUserId,
  useConfigContext,
  useGlobalsContext,
} from "shared/hooks";
import { IntegrationHandler } from "shared/integrations";
import { AppConfiguration, GenerationMode } from "shared/types";
import classNames from "shared/utils/classNames";

import { ChatDataSchema, StarterQuestionSchema } from "./schemas";
import { getRestoredChatData, openedInDashboard } from "./functions";
import useBotInfo from "./useBotInfo";
import useBotInfoUpdates from "./useBotInfoUpdates";
import useChatId from "./useChatId";
import useChatUserFormFields from "./useChatUserFormFields";
import useConnectSSE from "./useConnectSSE";
import useLocationBasedConsentInfo from "./useConsentIntegrationInfo";
import useEvents from "./useEvents";
import useHandleEvents from "./useHandleEvents";
import useInstalledApps from "./useInstalledApps";
import usePreserveChatData from "./usePreserveChatData";
import useScheduledActions from "./useScheduledActions";
import useStarterQuestions from "./useStarterQuestions";
import useStyleColors from "./useStyleColors";

import "./index.css";

type Props = {
  config: AppConfiguration;
};

const SonicBot = (props: Props) => {
  const {
    widgetOpen,
    widgetExpanded,
    setWidgetExpanded,
    parentPostMessage,
    sendAnalyticsEvent,
  } = useGlobalsContext();
  const { isIframe, instanceName, token } = useConfigContext();

  const { config } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const timeIdRef = useRef<NodeJS.Timeout>();

  const [isRenderWidget, setIsRenderWidget] = useState(true);
  const [message, setMessage] = useState("");
  const [chatData, setChatData] = useState<Output<typeof ChatDataSchema>>([]);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [generationMode, setGenerationMode] = useState<GenerationMode | null>(
    null,
  );

  const [popupClosedByUser, setPopupClosedByUser] = useState(false);
  // Delay for positioning
  const [renderPositionedWidget, setRenderPositionedWidget] = useState(false);

  const { locationBasedConsentInfo, loading: loadingLocationBasedConsentInfo } =
    useLocationBasedConsentInfo(config.token);

  const [messageLengthWhenSkipped, setMessageLengthWhenSkipped] =
    useState<number>(-1);

  const restoreSerializedRef = useRef(false);
  useLayoutEffect(() => {
    // To avoid React 18 double render locally
    if (restoreSerializedRef.current) {
      return;
    }
    restoreSerializedRef.current = true;

    const restoredChatData = getRestoredChatData(instanceName, token);
    if (restoredChatData) {
      setChatId(restoredChatData.chatId);
      setChatData(restoredChatData.chatData);
    }
    // Should run only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { actions } = useHandleEvents();
  useScheduledActions(actions);

  const {
    starterQuestions,
    setStarterQuestions,
    loading: loadingStarterQuestions,
    error: errorStarterQuestions,
  } = useStarterQuestions(config.token);

  const {
    chatUserFormFields,
    setChatUserFormFieldsUpdateTime,
    loading: loadingChatUserFormFields,
    error: errorChatUserFormFields,
  } = useChatUserFormFields(config.token);

  const { botInfo, updateBotInfoConfig } = useBotInfo(config.token);

  const { chatId, setChatId, resetChat } = useChatId(
    config.token,
    botInfo.id,
    config.chatId,
    eventSource,
    setChatData,
  );

  useEvents(
    botInfo,
    config.token,
    updateBotInfoConfig,
    setChatId,
    setStarterQuestions,
    setChatUserFormFieldsUpdateTime,
  );

  useBotInfoUpdates(botInfo);

  const { installedApps } = useInstalledApps(config.token);

  const widgetPosition = openedInDashboard(new URL(config.baseOrigin))
    ? "right"
    : botInfo.bot_info_config.widget_position;

  if (
    !loadingStarterQuestions &&
    !loadingChatUserFormFields &&
    !loadingLocationBasedConsentInfo
  ) {
    setTimeout(
      () => setRenderPositionedWidget(true),
      botInfo.bot_info_config?.widget_position === "right" ? 0 : 100,
    );
  }

  const { isLightAccent, styleColors } = useStyleColors(
    botInfo.bot_info_config.chatbot_color,
    widgetPosition,
  );

  const { connectSSE } = useConnectSSE(
    botInfo.id,
    config,
    botInfo.bot_info_config.support_contact_number,
    chatId,
    setGenerationMode,
    setChatData,
    setMessage,
    setEventSource,
  );

  usePreserveChatData(instanceName, token, chatData, chatId);

  const chatUserId = useChatUserId(botInfo?.id);
  const useAsPreview = !!config.targetElId;
  const classNameChatPanel = useMemo(() => {
    if (isIframe) {
      return classNames(
        "flex flex-1 flex-col overflow-hidden rounded-xl border border-solid border-supporting-divider/100",
      );
    }
    return classNames(
      "chat-panel-container",
      useAsPreview && "chat-panel-container-preview",
      !widgetExpanded && "chat-panel-container-hidden",
      widgetPosition === "left" && "chat-panel-container-left",
    );
  }, [isIframe, useAsPreview, widgetExpanded, widgetPosition]);

  useEffect(() => {
    if (widgetPosition !== "left") {
      return;
    }
    parentPostMessage("widget-position", "left");
  }, [parentPostMessage, widgetPosition]);

  const onSend = async (msg: string, chatUserId?: string) => {
    if (!msg || generationMode) {
      return;
    }
    sendAnalyticsEvent("Message Sent", {
      context: JSON.stringify({ message }),
    });

    setMessage("");
    connectSSE(msg ?? "", chatUserId)
      .then(setEventSource)
      // eslint-disable-next-line no-console
      .catch(err => console.error(err));
  };

  useEffect(() => {
    clearTimeout(timeIdRef.current);
    timeIdRef.current = setTimeout(() => {
      setIsRenderWidget(widgetExpanded);
    }, 100);
  }, [widgetExpanded]);

  const onIntegrationClick = (
    integration: IntegrationHandler,
  ): Promise<void> => {
    if (!widgetExpanded) {
      setWidgetExpanded(true);
    }
    return (
      connectSSE(
        integration?.name ?? "",
        chatUserId,
        undefined,
        true,
        integration?.type,
        integration?.data,
      )
        .then(setEventSource)
        // eslint-disable-next-line no-console
        .catch(err => console.error(err))
    );
  };

  const onSuggestionClick = async (
    starterQuestion: Output<typeof StarterQuestionSchema>,
  ) => {
    if (!widgetExpanded) {
      setWidgetExpanded(true);
    }

    sendAnalyticsEvent("Starter Question Clicked", {
      context: JSON.stringify({ question: starterQuestion?.question }),
    });

    return (
      connectSSE(
        starterQuestion?.question ?? "",
        chatUserId,
        starterQuestion?.id,
      )
        .then(setEventSource)
        // eslint-disable-next-line no-console
        .catch(err => console.error(err))
    );
  };

  const anyError = errorStarterQuestions || errorChatUserFormFields;

  if (!widgetOpen || !renderPositionedWidget) {
    return null;
  }

  const showPopupMessages =
    !widgetExpanded &&
    (botInfo.bot_info_config.show_starter_widget ||
      !!botInfo.bot_info_config.welcome_message);

  return (
    <div
      className={classNames(
        useAsPreview ? "bot-container-preview" : "",
        isIframe ? "bot-container_standalone" : "bot-container",
        widgetPosition === "left" && !isIframe && "bot-container-left",
      )}
      style={styleColors}
    >
      {anyError && <div className="max-w-[200px] text-ws-red">{anyError}</div>}
      {(isRenderWidget || isIframe) && (
        // maxwidth 920px
        <TransitionGroup
          className={
            isIframe ? "flex w-full max-w-[920px] flex-col py-0 md:py-8" : ""
          }
        >
          <CSSTransition
            nodeRef={wrapperRef}
            timeout={200}
            classNames="transition-wrapper"
            in
          >
            <div
              id="chat-panel"
              className={classNameChatPanel}
              ref={wrapperRef}
            >
              <ChatPanel
                botInfo={botInfo}
                message={message ?? ""}
                generationMode={generationMode}
                setGenerationMode={setGenerationMode}
                setMessage={setMessage}
                onSend={onSend}
                chatData={chatData}
                setChatData={setChatData}
                resetChat={resetChat}
                useAsPreview={useAsPreview}
                starterQuestions={starterQuestions}
                config={config}
                chatId={chatId}
                eventSource={eventSource}
                chatUserFormFields={chatUserFormFields}
                loadingChatUserFormFields={loadingChatUserFormFields}
                baseURL={config.serviceBaseUrl}
                token={config.token}
                widgetPosition={widgetPosition}
                onSuggestionClick={onSuggestionClick}
                onIntegrationClick={onIntegrationClick}
                locationBasedConsentInfo={locationBasedConsentInfo}
                messageLengthWhenSkipped={messageLengthWhenSkipped}
                setMessageLengthWhenSkipped={setMessageLengthWhenSkipped}
                installedApps={installedApps || []}
              />
            </div>
          </CSSTransition>
        </TransitionGroup>
      )}

      {/* do not show when standalone */}
      {(showPopupMessages || botInfo.bot_info_config.welcome_message) &&
        !isIframe && (
          <PopupMessages
            botInfo={botInfo}
            starterQuestions={starterQuestions}
            popupClosedByUser={popupClosedByUser}
            widgetPosition={widgetPosition}
            onSuggestionClick={onSuggestionClick}
            setPopupClosedByUser={setPopupClosedByUser}
            chatDataLength={chatData.length}
            generationMode={generationMode}
            isOverButton
          />
        )}

      {!isIframe && (
        <WidgetButton
          botInfo={botInfo}
          widgetPosition={widgetPosition}
          useAsPreview={useAsPreview}
          isLightAccent={isLightAccent}
          chatHasMessages={chatData.length > 1}
        />
      )}
    </div>
  );
};

export default SonicBot;
